import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import {
  apiFetch,
  type Kpis,
  type SegmentedCustomer,
  type ReportFile,
  type MonthlyYoy,
  type CustomersYearly,
  type VAGovtResponse,
  type HospitalUnit,
  type HospitalUnitsResponse,
} from "./api";

function useToken(): string | undefined {
  const auth = useAuth();
  return auth.user?.id_token ?? auth.user?.access_token;
}

export function useKpis(from: string, to: string, productGroups?: string[]) {
  const token = useToken();
  const pg = productGroups?.length ? `&product_group=${encodeURIComponent(productGroups.join(","))}` : "";
  return useQuery({
    queryKey: ["kpis", from, to, productGroups],
    enabled: !!token,
    queryFn: () => apiFetch<Kpis>(`/kpis?from=${from}&to=${to}${pg}`, token),
  });
}

export interface SegmentationResponse {
  period: { from: string; to: string };
  compare_period: { from: string; to: string };
  product_groups: string[];
  customers: SegmentedCustomer[];
}

export function useSegmentation(
  from: string,
  to: string,
  compareFrom: string,
  compareTo: string,
  productGroups?: string[],
) {
  const token = useToken();
  const pg = productGroups?.length ? `&product_group=${encodeURIComponent(productGroups.join(","))}` : "";
  return useQuery({
    queryKey: ["segmentation", from, to, compareFrom, compareTo, productGroups],
    enabled: !!token,
    queryFn: () =>
      apiFetch<SegmentationResponse>(
        `/customers/segmentation?from=${from}&to=${to}&compare_to_from=${compareFrom}&compare_to_to=${compareTo}${pg}`,
        token,
      ),
  });
}

export interface CustomerLocation {
  state: string | null;
  postal_code: string | null;
  country: string | null;
  primary_ship_to: string | null;
}

export interface CustomerWeekly {
  customer_id: string;
  customer_name: string;
  is_active: boolean;
  location: CustomerLocation | null;
  period: { from: string; to: string };
  weeks: { week: string; qty: number; revenue: number }[];
}

export interface CustomerSite {
  ship_to_name: string;
  state: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface CustomerLocationsResponse {
  customer_id: string;
  customer_name: string;
  locations: CustomerSite[];
}

export function useCustomerLocations(customerId: string | undefined) {
  const token = useToken();
  return useQuery({
    queryKey: ["customer-locations", customerId],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<CustomerLocationsResponse>(
        `/customers/${encodeURIComponent(customerId!)}/locations`,
        token,
      ),
  });
}

export function useCustomerWeekly(
  customerId: string | undefined,
  from: string,
  to: string,
  productGroups?: string[],
) {
  const token = useToken();
  const pg = productGroups?.length ? `&product_group=${encodeURIComponent(productGroups.join(","))}` : "";
  return useQuery({
    queryKey: ["customer-weekly", customerId, from, to, productGroups],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<CustomerWeekly>(
        `/customers/${encodeURIComponent(customerId!)}/weekly?from=${from}&to=${to}${pg}`,
        token,
      ),
  });
}

export interface DeviceUsage {
  customer_id: string;
  customer_name: string | null;
  period: { from: string; to: string };
  monitor_count: number;
  monthly: {
    month: string;
    catheter_qty: number;
    catheters_per_monitor: number | null;
  }[];
}

export function useCustomerDeviceUsage(
  customerId: string | undefined,
  from: string,
  to: string,
) {
  const token = useToken();
  return useQuery({
    queryKey: ["customer-device-usage", customerId, from, to],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<DeviceUsage>(
        `/customers/${encodeURIComponent(customerId!)}/device-usage?from=${from}&to=${to}`,
        token,
      ),
  });
}

export function useCustomerMonthlyYoy(
  customerId: string | undefined,
  productGroups?: string[],
) {
  const token = useToken();
  const pg = productGroups?.length
    ? `?product_group=${encodeURIComponent(productGroups.join(","))}`
    : "";
  return useQuery({
    queryKey: ["customer-monthly-yoy", customerId, productGroups],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<MonthlyYoy>(
        `/customers/${encodeURIComponent(customerId!)}/monthly-yoy${pg}`,
        token,
      ),
  });
}

export interface MapCustomer {
  customer_id: string;
  customer_name: string;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  primary_ship_to: string | null;
  ytd_revenue: number;
  ytd_qty: number;
}

export interface CustomersMapResponse {
  year_start: string;
  customers: MapCustomer[];
  unplaced_count: number;
}

export function useCustomersMap() {
  const token = useToken();
  return useQuery({
    queryKey: ["customers-map"],
    enabled: !!token,
    queryFn: () => apiFetch<CustomersMapResponse>("/customers/map", token),
  });
}

export function useCustomersYearly(productGroups?: string[]) {
  const token = useToken();
  const pg = productGroups?.length
    ? `?product_group=${encodeURIComponent(productGroups.join(","))}`
    : "";
  return useQuery({
    queryKey: ["customers-yearly", productGroups],
    enabled: !!token,
    queryFn: () => apiFetch<CustomersYearly>(`/customers/yearly${pg}`, token),
  });
}

export interface CustomerProfile {
  emr_system: string | null;
  epic_integrated: boolean | null;
  epic_integration_date: string | null;
  credentialing_status:
    | "not_started"
    | "in_progress"
    | "complete"
    | "refresh_needed"
    | null;
  credentialed_nurse_count: number | null;
  last_inservice_date: string | null;
  next_inservice_date: string | null;
  notes: string | null;
}

export interface CustomerProfileResponse {
  customer_id: string;
  customer_name: string;
  is_active: boolean;
  profile: CustomerProfile;
  updated_at: string | null;
  updated_by: string | null;
}

export function useCustomerProfile(customerId: string | undefined) {
  const token = useToken();
  return useQuery({
    queryKey: ["customer-profile", customerId],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<CustomerProfileResponse>(
        `/customers/${encodeURIComponent(customerId!)}/profile`,
        token,
      ),
  });
}

export async function saveCustomerProfile(
  token: string | undefined,
  customerId: string,
  payload: Partial<CustomerProfile> & { is_active?: boolean },
): Promise<CustomerProfileResponse> {
  return apiFetch<CustomerProfileResponse>(
    `/customers/${encodeURIComponent(customerId)}/profile`,
    token,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export interface CustomerContact {
  id?: number;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
}

export interface CustomerContactsResponse {
  customer_id: string;
  customer_name: string;
  contacts: (CustomerContact & { updated_at?: string | null; updated_by?: string | null })[];
}

export function useCustomerContacts(customerId: string | undefined) {
  const token = useToken();
  return useQuery({
    queryKey: ["customer-contacts", customerId],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<CustomerContactsResponse>(
        `/customers/${encodeURIComponent(customerId!)}/contacts`,
        token,
      ),
  });
}

export async function saveCustomerContacts(
  token: string | undefined,
  customerId: string,
  contacts: CustomerContact[],
): Promise<CustomerContactsResponse> {
  return apiFetch<CustomerContactsResponse>(
    `/customers/${encodeURIComponent(customerId)}/contacts`,
    token,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contacts }),
    },
  );
}

export interface SalesRep {
  id?: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  is_current: boolean;
  source?: string;
}

export interface SalesRepsResponse {
  customer_id: string;
  customer_name: string;
  sales_reps: SalesRep[];
}

export function useCustomerSalesReps(customerId: string | undefined) {
  const token = useToken();
  return useQuery({
    queryKey: ["customer-sales-reps", customerId],
    enabled: !!token && !!customerId,
    queryFn: () =>
      apiFetch<SalesRepsResponse>(
        `/customers/${encodeURIComponent(customerId!)}/sales-reps`,
        token,
      ),
  });
}

export async function saveCustomerSalesReps(
  token: string | undefined,
  customerId: string,
  sales_reps: SalesRep[],
): Promise<SalesRepsResponse> {
  return apiFetch<SalesRepsResponse>(
    `/customers/${encodeURIComponent(customerId)}/sales-reps`,
    token,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sales_reps }),
    },
  );
}

export type ActionPriority = "high" | "medium" | "low";

export interface ActionItem {
  id: string;
  customer_id: string;
  customer_name: string;
  type: string;
  priority: ActionPriority;
  title: string;
  detail: string;
  metric: number | null;
}

export interface ActionItemsResponse {
  generated_at: string;
  counts_by_priority: { high: number; medium: number; low: number };
  action_items: ActionItem[];
}

export function useActionItems() {
  const token = useToken();
  return useQuery({
    queryKey: ["action-items"],
    enabled: !!token,
    queryFn: () => apiFetch<ActionItemsResponse>("/action-items", token),
  });
}

export interface SilentCustomer {
  customer_id: string;
  customer_name: string;
  ytd_qty: number;
  last_order_date: string | null;
  days_since_last_order: number | null;
}

export interface SilentResponse {
  weeks: number;
  cutoff: string;
  product_groups: string[];
  customers: SilentCustomer[];
}

export function useSilentCustomers(weeks: number, productGroups?: string[]) {
  const token = useToken();
  const pg = productGroups?.length ? `&product_group=${encodeURIComponent(productGroups.join(","))}` : "";
  return useQuery({
    queryKey: ["silent-customers", weeks, productGroups],
    enabled: !!token,
    queryFn: () =>
      apiFetch<SilentResponse>(`/customers/silent?weeks=${weeks}${pg}`, token),
  });
}

export function useReportsList() {
  const token = useToken();
  return useQuery({
    queryKey: ["reports-list"],
    enabled: !!token,
    queryFn: () => apiFetch<{ reports: ReportFile[] }>("/reports/list", token),
  });
}

export interface ProductGroup {
  product_group: string;
  parent_group: string | null;
  display_order: number;
}

export function useProductGroups() {
  const token = useToken();
  return useQuery({
    queryKey: ["product-groups"],
    enabled: !!token,
    queryFn: () =>
      apiFetch<{ product_groups: ProductGroup[] }>("/product-groups", token),
  });
}

export async function fetchDownloadUrl(token: string | undefined, key: string): Promise<string> {
  const res = await apiFetch<{ url: string }>(
    `/reports/download-url?key=${encodeURIComponent(key)}`,
    token,
  );
  return res.url;
}

export function useAuthToken() {
  return useToken();
}

export function useVAGovt() {
  const token = useToken();
  return useQuery({
    queryKey: ["va-govt"],
    enabled: !!token,
    queryFn: () => apiFetch<VAGovtResponse>("/customers/va-govt", token),
  });
}

export function useHospitalUnits() {
  const token = useToken();
  return useQuery({
    queryKey: ["hospital-units"],
    enabled: !!token,
    queryFn: () => apiFetch<HospitalUnitsResponse>("/hospital-units", token),
  });
}

export async function saveHospitalUnit(
  token: string | undefined,
  id: number,
  payload: Partial<HospitalUnit>,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/hospital-units/${id}`, token, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
